import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { getStoredUser, isPremiumUser } from "../controllers/authController";
import {
  updatePassword,
  updateEmail,
  updatePhone,
  updateConsumerInfo,
  getConsumerInfo,
  updateUnitPrices,
} from "../controllers/userController";
import {
  getEmails,
  addEmail,
  deleteEmail,
  updateEmail as updateEmailAddress,
  getReportWindows,
  addReportWindow,
  deleteReportWindow,
  editReportWindow,
  getEnergyEfficiencySchedule,
  saveEnergyEfficiencySchedule,
} from "../controllers/emailController";
import {
  getClientMachines,
  updateMachineConfig,
  getClientProfileSettings,
  updateClientProfileSettings,
  getClientEmails,
  getMachineAlertEmails,
  setMachineAlertEmails,
} from "../controllers/machineController";
import NestedMachineSelector from "../components/NestedMachineSelector";
import ExcludeNodeTree from "../components/ExcludeNodeTree";
import api from "../utils/api";
import "./InfoTab.css";

const sections = [
  {
    id: "personal",
    title: "Personal Details",
    subtitle: "Update password, email, and phone",
  },
  {
    id: "machines",
    title: "Node Settings",
    subtitle: "Manage node configurations",
  },
  {
    id: "notifications",
    title: "Notifications",
    subtitle: "Update the notification settings",
  },
  {
    id: "multiTier",
    title: "Multi Tier",
    subtitle: "Add credentials for machines",
  },
];

const InfoTab = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(sections[0].id);
  const logoutCheckRef = useRef(false);
  const isPremium = isPremiumUser();

  // Check if user is still logged in on mount - redirect to login if not
  useEffect(() => {
    if (logoutCheckRef.current) return; // Already checked
    logoutCheckRef.current = true;

    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    if (!token || !user) {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  // Memoize storedUser to break the infinite re-render loop
  const storedUser = useMemo(() => getStoredUser(), []);
  const username = storedUser?.username || "";
  const clientName = storedUser?.client_name || username;

  // ----------------------------------------------------------------------
  // Section: Personal Details
  // ----------------------------------------------------------------------
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [emailForm, setEmailForm] = useState({
    currentEmail: storedUser?.email || "",
    newEmail: "",
  });
  const [phoneForm, setPhoneForm] = useState({
    countryCode: "+92",
    phoneNumber: storedUser?.phone || "",
  });

  const [consumerForm, setConsumerForm] = useState({
    consumerId: "",
    sanctionedLoad: "",
    unitPrice: "",
    peakUnitPrice: "",
    genUnitPrice: "",
    solarUnitPrice: "",
  });

  // Branch email notification state
  const [branchEmailForm, setBranchEmailForm] = useState({
    email: "",
    branch: "ALL",
  });
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [emailList, setEmailList] = useState({});
  const [displayEmailList, setDisplayEmailList] = useState([]); // Flattened list for display
  const [emailLoading, setEmailLoading] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null); // { originalEmail: string, newEmail: string, branch: string }

  // Report Windows state
  const [reportWindows, setReportWindows] = useState([]);
  const [reportWindowLoading, setReportWindowLoading] = useState(false);
  const [newWindow, setNewWindow] = useState({ start: "09:00", end: "17:00" });
  const [editingWindow, setEditingWindow] = useState(null); // { oldStart: string, oldEnd: string, newStart: string, newEnd: string }

  // Energy Efficiency Report Schedule
  const [effSchedule, setEffSchedule] = useState([]); // [{ start, end, emails, last_sent }]
  const [effLoading, setEffLoading] = useState(false);
  const [effSaving, setEffSaving] = useState(false);
  const [effEmailInput, setEffEmailInput] = useState({}); // per-entry email text input

  // Toast notification state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const toastTimer = useRef(null);

  const showToast = (message, type = "success") => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ show: true, message, type });
    toastTimer.current = setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3500);
  };

  // Fetch consumer info on mount
  useEffect(() => {
    if (username) {
      getConsumerInfo(username)
        .then((data) => {
          setConsumerForm({
            consumerId: data.consumer_id || "",
            sanctionedLoad: data.sanctioned_load || "",
            unitPrice: data.unit_price || "",
            peakUnitPrice: data.peak_unit_price || "",
            genUnitPrice: data.gen_unitPrice || data.gen_unitprice || "",
            solarUnitPrice: data.solar_unitPrice || data.solar_unitprice || "",
          });
        })
        .catch(() => {
          // Consumer info not found yet, keep defaults
        });
    }
  }, [username]);

  // Fetch hierarchy data for branches
  useEffect(() => {
    if (username) {
      setBranchesLoading(true);
      api.get(`/hierarchy-tree?username=${encodeURIComponent(username)}`)
        .then((res) => {
          const data = res.data;
          const gridVirtualMachines = [];

          const extractMachines = (node) => {
            if (!node) return;
            if (node.machine_name && node.category === 'GRID') {
              gridVirtualMachines.push(node.machine_name);
            }
            if (node.children) {
              if (Array.isArray(node.children)) {
                node.children.forEach(extractMachines);
              } else if (typeof node.children === "object") {
                Object.values(node.children).forEach(extractMachines);
              }
            }
          };

          extractMachines(data);

          const uniqueBranches = Array.from(new Set(gridVirtualMachines));
          // Add "ALL" as first option
          setBranches(["ALL", ...uniqueBranches]);
        })
        .catch((error) => {
          console.error("Error fetching hierarchy-tree:", error);
          setBranches(["ALL"]); // At least show ALL option
        })
        .finally(() => {
          setBranchesLoading(false);
        });
    }
  }, [username]);

  const fetchEmails = async () => {
    setEmailLoading(true);
    try {
      const data = await getEmails(username);
      if (data && data.emails) {
        setEmailList(data.emails);

        // Flatten for easier display
        const flatList = [];
        Object.entries(data.emails).forEach(([branch, emails]) => {
          emails.forEach(email => {
            flatList.push({ branch, email });
          });
        });
        setDisplayEmailList(flatList);
      }
    } catch (error) {
      console.error("Failed to fetch emails:", error);
    } finally {
      setEmailLoading(false);
    }
  };

  useEffect(() => {
    if (username) {
      fetchEmails();
    }
  }, [username]);

  const handleConsumerInfoUpdate = async () => {
    if (!consumerForm.consumerId.trim()) {
      showToast("Please enter a Consumer ID.", "error");
      return;
    }
    if (
      !consumerForm.sanctionedLoad ||
      Number(consumerForm.sanctionedLoad) <= 0
    ) {
      showToast("Please enter a valid Sanctioned Load.", "error");
      return;
    }
    try {
      const res = await updateConsumerInfo(
        username,
        consumerForm.consumerId,
        consumerForm.sanctionedLoad,
      );
      showToast(
        res.message || "Customer information saved successfully",
        "success",
      );
    } catch (error) {
      showToast(
        error.response?.data?.message ||
        "Failed to update customer information",
        "error",
      );
    }
  };

  const handleUnitPricesUpdate = async () => {
    try {
      const res = await updateUnitPrices(
        username,
        consumerForm.unitPrice,
        consumerForm.genUnitPrice,
        consumerForm.solarUnitPrice,
        consumerForm.peakUnitPrice,
      );
      showToast(
        res.message || "Unit prices updated successfully",
        "success",
      );
    } catch (error) {
      showToast(
        error.response?.data?.message ||
        "Failed to update unit prices",
        "error",
      );
    }
  };

  const handlePasswordUpdate = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showToast("New password and confirm password do not match.", "error");
      return;
    }
    try {
      const res = await updatePassword(
        username,
        passwordForm.oldPassword,
        passwordForm.newPassword,
        passwordForm.confirmPassword,
      );
      showToast(res.message || "Password updated successfully", "success");
      setPasswordForm({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to update password",
        "error",
      );
    }
  };

  const handleEmailUpdate = async () => {
    try {
      const res = await updateEmail(
        username,
        emailForm.currentEmail,
        emailForm.newEmail,
      );
      showToast(res.message || "Email updated successfully", "success");
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to update email",
        "error",
      );
    }
  };

  const handlePhoneUpdate = async () => {
    try {
      const res = await updatePhone(
        username,
        phoneForm.countryCode,
        phoneForm.phoneNumber,
      );
      showToast(res.message || "Phone number updated successfully", "success");
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to update phone number",
        "error",
      );
    }
  };

  const handleBranchEmailUpdate = async () => {
    // Validate email
    if (!branchEmailForm.email.trim()) {
      showToast("Please enter an email address.", "error");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(branchEmailForm.email)) {
      showToast("Please enter a valid email address.", "error");
      return;
    }

    // Validate branch selection
    if (!branchEmailForm.branch) {
      showToast("Please select a branch.", "error");
      return;
    }

    try {
      const data = await addEmail(username, branchEmailForm.email, branchEmailForm.branch);
      showToast(
        data.message || "Email added successfully for branch notifications",
        "success",
      );
      // Reset form and refresh list
      setBranchEmailForm({ email: "", branch: "ALL" });
      fetchEmails();
    } catch (error) {
      showToast(error.response?.data?.detail || error.message || "Failed to add email for branch", "error");
    }
  };

  const handleDeleteEmail = async (email, branch) => {
    if (!window.confirm(`Are you sure you want to delete ${email} from ${branch}?`)) return;

    try {
      const data = await deleteEmail(username, email, branch);
      showToast(data.message || "Email deleted successfully", "success");
      fetchEmails();
    } catch (error) {
      showToast(error.response?.data?.detail || "Failed to delete email", "error");
    }
  };

  const startEditingEmail = (email, branch) => {
    setEditingEmail({ originalEmail: email, newEmail: email, branch });
  };

  const cancelEditingEmail = () => {
    setEditingEmail(null);
  };

  const saveEditedEmail = async () => {
    if (!editingEmail || !editingEmail.newEmail.trim()) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editingEmail.newEmail)) {
      showToast("Please enter a valid email address.", "error");
      return;
    }

    if (editingEmail.newEmail === editingEmail.originalEmail) {
      setEditingEmail(null);
      return;
    }

    try {
      const data = await updateEmailAddress(username, editingEmail.originalEmail, editingEmail.newEmail);
      showToast(data.message || "Email updated successfully", "success");
      setEditingEmail(null);
      fetchEmails();
    } catch (error) {
      showToast(error.response?.data?.detail || "Failed to update email", "error");
    }
  };

  // ----------------------------------------------------------------------
  // Section: Report Windows Helpers & Handlers
  // ----------------------------------------------------------------------

  const fetchReportWindows = useCallback(async () => {
    console.log("Fetching report windows for:", username);
    if (!username) return;
    setReportWindowLoading(true);
    try {
      const data = await getReportWindows(username);
      console.log("Report windows data:", data);
      setReportWindows(data.report_windows || []);
    } catch (error) {
      console.error("Failed to fetch report windows:", error);
    } finally {
      setReportWindowLoading(false);
    }
  }, [username]);

  const fetchEffSchedule = useCallback(async () => {
    if (!username) return;
    setEffLoading(true);
    try {
      const schedule = await getEnergyEfficiencySchedule(username);
      setEffSchedule(Array.isArray(schedule) ? schedule : []);
    } catch (error) {
      console.error("Failed to fetch energy efficiency schedule:", error);
      setEffSchedule([]);
    } finally {
      setEffLoading(false);
    }
  }, [username]);

  useEffect(() => {
    console.log("Active Section:", activeSection, "Username:", username);
    if (activeSection === "notifications" && username) {
      fetchReportWindows();
      fetchProfileSettings();
      fetchEffSchedule();
    }
  }, [activeSection, username, fetchReportWindows, fetchEffSchedule]);

  // ---- Energy Efficiency Report Schedule handlers ----
  const isValidEmailAddr = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

  const addEffEntry = () => {
    setEffSchedule((prev) => [
      ...prev,
      { start: "09:00", end: "17:00", emails: [], last_sent: null },
    ]);
  };

  const removeEffEntry = (idx) => {
    setEffSchedule((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateEffTime = (idx, field, value) => {
    setEffSchedule((prev) =>
      prev.map((e, i) => (i === idx ? { ...e, [field]: value } : e)),
    );
  };

  const addEffEmail = (idx) => {
    const raw = (effEmailInput[idx] || "").trim();
    if (!raw) return;
    if (!isValidEmailAddr(raw)) {
      showToast("Invalid email format", "error");
      return;
    }
    setEffSchedule((prev) =>
      prev.map((e, i) => {
        if (i !== idx) return e;
        if (e.emails.includes(raw)) return e;
        return { ...e, emails: [...e.emails, raw] };
      }),
    );
    setEffEmailInput((prev) => ({ ...prev, [idx]: "" }));
  };

  const removeEffEmail = (idx, email) => {
    setEffSchedule((prev) =>
      prev.map((e, i) =>
        i === idx ? { ...e, emails: e.emails.filter((x) => x !== email) } : e,
      ),
    );
  };

  const handleSaveEffSchedule = async () => {
    // Validate: each entry needs times + at least one email
    for (let i = 0; i < effSchedule.length; i++) {
      const e = effSchedule[i];
      if (!e.start || !e.end) {
        showToast(`Schedule ${i + 1}: set both start and end time`, "error");
        return;
      }
      if (!e.emails || e.emails.length === 0) {
        showToast(`Schedule ${i + 1}: add at least one email`, "error");
        return;
      }
    }
    setEffSaving(true);
    try {
      await saveEnergyEfficiencySchedule(username, effSchedule);
      showToast("Energy efficiency schedule saved", "success");
      fetchEffSchedule();
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to save schedule",
        "error",
      );
    } finally {
      setEffSaving(false);
    }
  };

  // Convert "HH:mm" (24h) to "hh AM/PM"
  const formatTimeForBackend = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    let h = parseInt(hours, 10);
    const m = minutes || "00";
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12;
    h = h ? h : 12; // the hour '0' should be '12'
    return `${h.toString().padStart(2, "0")} ${ampm}`;
  };

  // Convert "hh AM/PM" to "HH:mm" (24h) for input
  const formatTimeForInput = (timeStr) => {
    if (!timeStr) return "";
    // Expected format: "09 AM" or "05 PM"
    const match = timeStr.match(/(\d+)\s+(AM|PM)/i);
    if (!match) return timeStr; // Fallback if format is different
    let [_, h, ampm] = match;
    h = parseInt(h, 10);
    if (ampm.toUpperCase() === "PM" && h < 12) h += 12;
    if (ampm.toUpperCase() === "AM" && h === 12) h = 0;
    return `${h.toString().padStart(2, "0")}:00`;
  };

  const handleAddReportWindow = async () => {
    try {
      const startFormatted = formatTimeForBackend(newWindow.start);
      const endFormatted = formatTimeForBackend(newWindow.end);

      await addReportWindow(username, startFormatted, endFormatted);
      showToast("Report window added successfully", "success");
      fetchReportWindows();
      setNewWindow({ start: "09:00", end: "17:00" }); // Reset to default
    } catch (error) {
      showToast(error.response?.data?.detail || "Failed to add report window", "error");
    }
  };

  const handleDeleteReportWindow = async (reportWindow) => {
    if (!window.confirm(`Delete report window ${reportWindow.start} - ${reportWindow.end}?`)) return;
    try {
      await deleteReportWindow(username, reportWindow.start, reportWindow.end);
      showToast("Report window deleted successfully", "success");
      fetchReportWindows();
    } catch (error) {
      showToast(error.response?.data?.detail || "Failed to delete report window", "error");
    }
  };

  const startEditingReportWindow = (window) => {
    setEditingWindow({
      oldStart: window.start,
      oldEnd: window.end,
      newStart: formatTimeForInput(window.start),
      newEnd: formatTimeForInput(window.end),
    });
  };

  const cancelEditingReportWindow = () => {
    setEditingWindow(null);
  };

  const saveEditedReportWindow = async () => {
    if (!editingWindow) return;
    try {
      const newStartFormatted = formatTimeForBackend(editingWindow.newStart);
      const newEndFormatted = formatTimeForBackend(editingWindow.newEnd);

      await editReportWindow(
        username,
        editingWindow.oldStart,
        editingWindow.oldEnd,
        newStartFormatted,
        newEndFormatted
      );
      showToast("Report window updated successfully", "success");
      setEditingWindow(null);
      fetchReportWindows();
    } catch (error) {
      showToast(error.response?.data?.detail || "Failed to update report window", "error");
    }
  };

  // ----------------------------------------------------------------------
  // Section: Logo Upload
  // ----------------------------------------------------------------------
  const [logoImage, setLogoImage] = useState(null);
  const [logoDirty, setLogoDirty] = useState(false);
  const [logoLoading, setLogoLoading] = useState(true);
  const [logoExists, setLogoExists] = useState(false);
  const [remoteLogo, setRemoteLogo] = useState(null);
  const fileInputRef = useRef(null);

  const currentLogoDisplay = logoImage || remoteLogo;

  // Fetch logo on mount
  useEffect(() => {
    if (username) {
      setLogoLoading(true);
      fetch(`/api/get_logo?username=${encodeURIComponent(username)}`)
        .then((response) => {
          if (response.ok) {
            return response.blob();
          } else if (response.status === 404) {
            // No logo found
            setLogoExists(false);
            setRemoteLogo(null);
            return null;
          } else {
            throw new Error("Failed to fetch logo");
          }
        })
        .then((blob) => {
          if (blob) {
            const logoUrl = URL.createObjectURL(blob);
            setRemoteLogo(logoUrl);
            setLogoExists(true);
          }
        })
        .catch((error) => {
          console.error("Error fetching logo:", error);
          setLogoExists(false);
          setRemoteLogo(null);
        })
        .finally(() => {
          setLogoLoading(false);
        });
    }
  }, [username]);

  const handleLogoSelect = (file) => {
    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      showToast("Please select a PNG, JPG, or JPEG image.", "error");
      return;
    }

    // Validate file size (max 100KB)
    const maxSize = 100 * 1024; // 100KB in bytes
    if (file.size > maxSize) {
      showToast(
        "File size must be less than 100KB. Please select a smaller image.",
        "error",
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const nextLogo = event.target?.result;
      if (typeof nextLogo === "string") {
        setLogoImage(nextLogo);
        setLogoDirty(true);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleLogoSubmit = async () => {
    if (!logoImage) {
      showToast("Please select an image first.", "error");
      return;
    }

    try {
      const blob = await fetch(logoImage).then((res) => res.blob());

      // Validate blob size again before upload
      if (blob.size > 100 * 1024) {
        showToast(
          "File size must be less than 100KB. Please select a smaller image.",
          "error",
        );
        return;
      }

      const formData = new FormData();
      formData.append("file", blob, "logo.png");
      formData.append("username", username);

      const response = await fetch(`/api/upload_logo`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setLogoDirty(false);
        showToast("Logo uploaded successfully!", "success");

        // Clear the preview image
        setLogoImage(null);

        // Automatically refresh the page so that the Header and all other
        // components fetch the updated logo.
        setTimeout(() => window.location.reload(), 1500);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to upload logo");
      }
    } catch (error) {
      console.error("Upload error:", error);
      showToast("Error uploading logo: " + error.message, "error");
    }
  };

  // ----------------------------------------------------------------------
  // Section: Machine Settings
  // ----------------------------------------------------------------------
  const [machines, setMachines] = useState([]);
  const [machineLoading, setMachineLoading] = useState(false);
  const [expandAll, setExpandAll] = useState(false);
  const [clientEmailSuggestions, setClientEmailSuggestions] = useState([]);

  useEffect(() => {
    if (activeSection === "machines" && username) {
      setMachineLoading(true);
      getClientMachines(username)
        .then((data) => {
          setMachines(data.machines || []);
        })
        .catch((err) => {
          console.error("Failed to fetch machines", err);
        })
        .finally(() => setMachineLoading(false));

      getClientEmails(username)
        .then((emails) => setClientEmailSuggestions(emails || []))
        .catch((err) => {
          console.error("Failed to fetch client emails", err);
          setClientEmailSuggestions([]);
        });
    }
  }, [activeSection, username]);

  const handleMachineUpdate = async (machineId, updatedFields) => {
    const machine = machines.find((m) => m.machine_id === machineId);
    if (!machine) return;

    try {
      await updateMachineConfig(machine.meter_id, {
        username,
        ...updatedFields,
      });
      // Update local state
      setMachines((prev) =>
        prev.map((m) =>
          m.machine_id === machineId ? { ...m, ...updatedFields } : m,
        ),
      );
      showToast("Machine configuration updated successfully", "success");
    } catch (error) {
      showToast(
        error.response?.data?.message ||
        "Failed to update machine configuration",
        "error",
      );
    }
  };

  // ----------------------------------------------------------------------
  // Section: Notifications
  // ----------------------------------------------------------------------
  const [notificationPrefs, setNotificationPrefs] = useState({
    emailAlerts: true,
    smsAlerts: false,
    weeklyDigest: true,
  });

  const [profileSettings, setProfileSettings] = useState({
    sanctioned_load: null,
    sanctioned_load_on: false,
    minimum_threshold: null,
    minimum_threshold_on: false,
  });
  const [localThreshold, setLocalThreshold] = useState("");
  const [togglingField, setTogglingField] = useState(null);

  const fetchProfileSettings = async () => {
    try {
      const data = await getClientProfileSettings(username);
      setProfileSettings(data);
      setLocalThreshold(data.minimum_threshold ?? "");
    } catch (e) {
      console.error("Failed to fetch profile settings:", e);
    }
  };

  const handleToggleSetting = async (field, extraPayload) => {
    const newVal = !profileSettings[field];
    setProfileSettings(prev => ({ ...prev, [field]: newVal }));
    setTogglingField(field);
    try {
      await updateClientProfileSettings(username, { [field]: newVal, ...extraPayload });
      showToast("Setting updated successfully", "success");
    } catch (e) {
      setProfileSettings(prev => ({ ...prev, [field]: !newVal }));
      showToast("Failed to update setting", "error");
    } finally {
      setTogglingField(null);
    }
  };

  // ----------------------------------------------------------------------
  // Section: Multi Tier (Add Credentials)
  // ----------------------------------------------------------------------
  const [multiTierForm, setMultiTierForm] = useState({
    username: "",
    password: "",
    machineName: "",
    meterId: "",
    excludedIds: [],
  });
  const [mtMachinesTree, setMtMachinesTree] = useState([]);
  const [mtSelectedNodeObj, setMtSelectedNodeObj] = useState(null);
  const [mtLoading, setMtLoading] = useState(false);
  const [addedCredentials, setAddedCredentials] = useState([]);

  useEffect(() => {
    if (activeSection === "multiTier" && username) {
      setMtLoading(true);
      api.get(`/hierarchy-tree?username=${encodeURIComponent(username)}`)
        .then((res) => {
          const data = res.data;

          // Helper to transform backend tree to NestedMachineSelector format
          const transformTree = (node) => {
            if (!node) return [];
            const rawChildren = Array.isArray(node.children)
              ? node.children
              : (node.children ? Object.values(node.children) : []);

            return rawChildren.map(child => ({
              name: child.machine_name,
              category: child.category,
              meter_id: child.meter_id,
              children: transformTree(child),
              // pass raw object too, purely to power ExcludeNodeTree later
              rawNode: child
            }));
          };

          setMtMachinesTree(transformTree(data));
        })
        .catch((err) => {
          console.error("Error fetching multi tier machines:", err);
          showToast("Failed to load machines", "error");
        })
        .finally(() => {
          setMtLoading(false);
        });
    }
  }, [activeSection, username]);

  const handleAddCredentials = async () => {
    if (!multiTierForm.username || !multiTierForm.password || !multiTierForm.meterId) {
      showToast("Please fill all fields", "error");
      return;
    }

    try {
      const payload = {
        client_name: clientName,
        username: multiTierForm.username,
        password: multiTierForm.password,
        email: `${multiTierForm.username}@example.com`,
        is_root_user: false,
        scope_meter_id: multiTierForm.meterId,
        exclude_scope_meter_id: multiTierForm.excludedIds.length > 0 ? multiTierForm.excludedIds.join(',') : null,
      };

      await api.post("/add_user", payload);

      showToast("Credentials added successfully", "success");

      // Add to the history list
      setAddedCredentials(prev => [
        ...prev,
        {
          username: multiTierForm.username,
          machineName: multiTierForm.machineName,
          timestamp: new Date().toLocaleTimeString()
        }
      ]);

      setMultiTierForm({ username: "", password: "", machineName: "", meterId: "", excludedIds: [] });
      setMtSelectedNodeObj(null);
    } catch (err) {
      console.error(err);
      showToast(err.message || "Error adding credentials", "error");
    }
  };

  // ----------------------------------------------------------------------
  // Renderers
  // ----------------------------------------------------------------------
  const renderSectionContent = () => {
    switch (activeSection) {
      case "personal":
        return (
          <div className="info-content-card">
            <header style={contentHeaderStyle}>
              <div>
                <h2 style={contentTitleStyle}>Personal Details</h2>
                <p
                  style={{ margin: "4px 0 0", color: "#94a3b8", fontSize: 14 }}
                >
                  Manage your account information, consumer details, and
                  branding
                </p>
              </div>
            </header>

            <div className="info-personal-groups">
              <section className="info-personal-group">
                <div className="info-personal-group-header">
                  <h3 style={sectionBlockTitleStyle}>Consumer Energy Details</h3>
                  <p style={sectionBlockDescriptionStyle}>Consumer details and pricing</p>
                </div>
                <div className="info-personal-subsections">
                  {/* Consumer Information */}
                  <div className="info-personal-block">
                    <h3 style={sectionBlockTitleStyle}>Consumer Information</h3>
                    <div style={formGridStyle}>
                      <label style={inputGroupStyle}>
                        Consumer ID
                        <input
                          type="text"
                          value={consumerForm.consumerId}
                          onChange={(e) =>
                            setConsumerForm({
                              ...consumerForm,
                              consumerId: e.target.value,
                            })
                          }
                          style={textInputStyle}
                          placeholder="Enter your Consumer ID"
                        />
                      </label>
                      <label style={inputGroupStyle}>
                        Sanctioned Load (kW)
                        <input
                          type="number"
                          step="0.1"
                          value={consumerForm.sanctionedLoad}
                          onChange={(e) =>
                            setConsumerForm({
                              ...consumerForm,
                              sanctionedLoad: e.target.value,
                            })
                          }
                          style={textInputStyle}
                          placeholder="e.g. 5.0"
                        />
                      </label>
                    </div>
                    <div style={{ marginTop: 12, textAlign: "right" }}>
                      <button
                        type="button"
                        onClick={handleConsumerInfoUpdate}
                        style={primaryButtonStyle}
                      >
                        Update Details
                      </button>
                    </div>
                  </div>

                  {/* Unit Prices */}
                  <div className="info-personal-block">
                    <h3 style={sectionBlockTitleStyle}>Unit Prices</h3>
                    <div style={formGridStyle}>
                      <label style={inputGroupStyle}>
                        Grid Unit Price
                        <input
                          type="number"
                          step="1"
                          value={consumerForm.unitPrice}
                          onChange={(e) =>
                            setConsumerForm({
                              ...consumerForm,
                              unitPrice: e.target.value,
                            })
                          }
                          style={textInputStyle}
                          placeholder="e.g. 50"
                        />
                      </label>
                      <label style={inputGroupStyle}>
                        Grid Peak Unit Price
                        <input
                          type="number"
                          step="1"
                          value={consumerForm.peakUnitPrice}
                          onChange={(e) =>
                            setConsumerForm({
                              ...consumerForm,
                              peakUnitPrice: e.target.value,
                            })
                          }
                          style={textInputStyle}
                          placeholder="e.g. 80"
                        />
                      </label>
                      <label style={inputGroupStyle}>
                        Generator Unit Price
                        <input
                          type="number"
                          step="1"
                          value={consumerForm.genUnitPrice}
                          onChange={(e) =>
                            setConsumerForm({
                              ...consumerForm,
                              genUnitPrice: e.target.value,
                            })
                          }
                          style={textInputStyle}
                          placeholder="e.g. 80"
                        />
                      </label>
                      <label style={inputGroupStyle}>
                        Solar Unit Price
                        <input
                          type="number"
                          step="1"
                          value={consumerForm.solarUnitPrice}
                          onChange={(e) =>
                            setConsumerForm({
                              ...consumerForm,
                              solarUnitPrice: e.target.value,
                            })
                          }
                          style={textInputStyle}
                          placeholder="e.g. 25"
                        />
                      </label>
                    </div>
                    <div style={{ marginTop: 12, textAlign: "right" }}>
                      <button
                        type="button"
                        onClick={handleUnitPricesUpdate}
                        style={primaryButtonStyle}
                      >
                        Update Prices
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              <section className="info-personal-group">
                <div className="info-personal-group-header">
                  <h3 style={sectionBlockTitleStyle}>Security & Contact</h3>
                  <p style={sectionBlockDescriptionStyle}>Password, phone, and email notification settings</p>
                </div>

                {/* Password Update */}
                <div className="info-personal-block">
                  <h3 style={sectionBlockTitleStyle}>Update Password</h3>
                  <div style={formGridStyle}>
                    <label style={inputGroupStyle}>
                      Old Password
                      <input
                        type="password"
                        value={passwordForm.oldPassword}
                        onChange={(e) =>
                          setPasswordForm({
                            ...passwordForm,
                            oldPassword: e.target.value,
                          })
                        }
                        style={textInputStyle}
                      />
                    </label>
                    <label style={inputGroupStyle}>
                      New Password
                      <input
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) =>
                          setPasswordForm({
                            ...passwordForm,
                            newPassword: e.target.value,
                          })
                        }
                        style={textInputStyle}
                      />
                    </label>
                    <label style={inputGroupStyle}>
                      Confirm New Password
                      <input
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) =>
                          setPasswordForm({
                            ...passwordForm,
                            confirmPassword: e.target.value,
                          })
                        }
                        style={textInputStyle}
                      />
                    </label>
                  </div>
                  <div style={{ marginTop: 12, textAlign: "right" }}>
                    <button
                      type="button"
                      onClick={handlePasswordUpdate}
                      style={primaryButtonStyle}
                    >
                      Update Password
                    </button>
                  </div>
                </div>

                {/* Phone Update (Premium Feature) */}
                <div
                  className="info-personal-block"
                  style={{
                    position: "relative",
                    opacity: isPremium ? 1 : 0.55,
                    pointerEvents: isPremium ? "auto" : "none",
                    userSelect: isPremium ? "auto" : "none",
                  }}
                  aria-disabled={!isPremium}
                >
                  {!isPremium && (
                    <div
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        background: "linear-gradient(135deg, #f59e0b, #d97706)",
                        color: "#fff",
                        padding: "4px 10px",
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                        pointerEvents: "auto",
                        zIndex: 2,
                      }}
                      title="Upgrade to Premium to unlock this feature"
                    >
                      PREMIUM FEATURE
                    </div>
                  )}
                  <h3 style={sectionBlockTitleStyle}>Update Phone Number</h3>
                  <div style={formGridStyle}>
                    <label style={inputGroupStyle}>
                      Country Code
                      <select
                        value={phoneForm.countryCode}
                        onChange={(e) =>
                          setPhoneForm({
                            ...phoneForm,
                            countryCode: e.target.value,
                          })
                        }
                        style={textInputStyle}
                      >
                        <option value="+92">+92 (Pakistan)</option>
                        <option value="+1">+1 (USA)</option>
                        <option value="+44">+44 (UK)</option>
                      </select>
                    </label>
                    <label style={inputGroupStyle}>
                      Phone Number
                      <input
                        type="tel"
                        value={phoneForm.phoneNumber}
                        onChange={(e) =>
                          setPhoneForm({
                            ...phoneForm,
                            phoneNumber: e.target.value,
                          })
                        }
                        style={textInputStyle}
                      />
                    </label>
                  </div>
                  <div style={{ marginTop: 12, textAlign: "right" }}>
                    <button
                      type="button"
                      onClick={handlePhoneUpdate}
                      style={primaryButtonStyle}
                    >
                      Update Phone
                    </button>
                  </div>
                </div>

                {/* Email Notifications by Branch (Premium Feature) */}
                <div
                  className="info-personal-block"
                  style={{
                    position: "relative",
                    opacity: isPremium ? 1 : 0.55,
                    pointerEvents: isPremium ? "auto" : "none",
                    userSelect: isPremium ? "auto" : "none",
                  }}
                  aria-disabled={!isPremium}
                >
                  {!isPremium && (
                    <div
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        background: "linear-gradient(135deg, #f59e0b, #d97706)",
                        color: "#fff",
                        padding: "4px 10px",
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                        pointerEvents: "auto",
                        zIndex: 2,
                      }}
                      title="Upgrade to Premium to unlock this feature"
                    >
                      PREMIUM FEATURE
                    </div>
                  )}
                  <h3 style={sectionBlockTitleStyle}>
                    Email Notifications by Branch
                  </h3>
                  <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14 }}>
                    Add email addresses to receive notifications for specific
                    branches
                  </p>
                  <div style={formGridStyle}>
                    <label style={inputGroupStyle}>
                      Email Address
                      <input
                        type="email"
                        value={branchEmailForm.email}
                        onChange={(e) =>
                          setBranchEmailForm({
                            ...branchEmailForm,
                            email: e.target.value,
                          })
                        }
                        style={textInputStyle}
                        placeholder="Enter email address"
                      />
                    </label>
                    <label style={inputGroupStyle}>
                      Branch
                      <select
                        value={branchEmailForm.branch}
                        onChange={(e) =>
                          setBranchEmailForm({
                            ...branchEmailForm,
                            branch: e.target.value,
                          })
                        }
                        style={textInputStyle}
                        disabled={branchesLoading}
                      >
                        {branchesLoading && (
                          <option value="ALL">Loading branches...</option>
                        )}
                        {branches.map((branch) => (
                          <option key={branch} value={branch}>
                            {branch}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div style={{ marginTop: 12, textAlign: "right" }}>
                    <button
                      type="button"
                      onClick={handleBranchEmailUpdate}
                      style={primaryButtonStyle}
                      disabled={branchesLoading}
                    >
                      Add Email for Branch
                    </button>
                  </div>

                  {/* Email List */}
                  {emailLoading ? (
                    <p>Loading emails...</p>
                  ) : (
                    <div style={{ marginTop: 16 }}>
                      {displayEmailList.length === 0 ? (
                        <p style={{ color: "#94a3b8", fontSize: 14 }}>No emails added yet.</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {displayEmailList.map((item, index) => {
                            const isEditing = editingEmail && editingEmail.originalEmail === item.email && editingEmail.branch === item.branch;

                            return (
                              <div key={`${item.branch}-${item.email}-${index}`} style={{
                                padding: '10px 14px',
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 8,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: 14
                              }}>
                                {isEditing ? (
                                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
                                    <input
                                      type="email"
                                      value={editingEmail.newEmail}
                                      onChange={(e) => setEditingEmail({ ...editingEmail, newEmail: e.target.value })}
                                      style={{ ...textInputStyle, padding: '6px 8px', flex: 1 }}
                                    />
                                    <button
                                      onClick={saveEditedEmail}
                                      style={{ ...primaryButtonStyle, padding: '6px 12px', fontSize: 12, background: '#16a34a' }}
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={cancelEditingEmail}
                                      style={{ ...primaryButtonStyle, padding: '6px 12px', fontSize: 12, background: '#94a3b8' }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                      <span style={{ fontWeight: 500, color: '#334155' }}>{item.email}</span>
                                      <span style={{ fontSize: 12, color: '#64748b' }}>Branch: {item.branch}</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                      <button
                                        onClick={() => startEditingEmail(item.email, item.branch)}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          color: '#2563eb',
                                          cursor: 'pointer',
                                          fontSize: 13,
                                          fontWeight: 500
                                        }}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => handleDeleteEmail(item.email, item.branch)}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          color: '#ef4444',
                                          cursor: 'pointer',
                                          fontSize: 13,
                                          fontWeight: 500
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </section>

              <section className="info-personal-group">
                <div className="info-personal-group-header">
                  <h3 style={sectionBlockTitleStyle}>Branding</h3>
                  <p style={sectionBlockDescriptionStyle}>Company logo and visual identity</p>
                </div>

                {/* Logo Upload */}
                <div className="info-personal-block">
                  <h3 style={sectionBlockTitleStyle}>Company Logo</h3>
                  {logoLoading ? (
                    <div style={{ textAlign: "center", padding: "40px 20px" }}>
                      <p style={{ color: "#64748b" }}>Loading logo...</p>
                    </div>
                  ) : (
                    <>
                      <div style={logoUploadWrapperStyle}>
                        {currentLogoDisplay ? (
                          <div style={logoDisplayWrapperStyle}>
                            <img
                              src={currentLogoDisplay}
                              alt="Company logo"
                              style={logoDisplayStyle}
                            />
                            <button
                              type="button"
                              style={editLogoButtonStyle}
                              onClick={() => fileInputRef.current?.click()}
                            >
                              Update Logo
                            </button>
                          </div>
                        ) : (
                          <div
                            style={logoDropAreaStyle}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <span>Click to upload logo</span>
                          </div>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/jpg"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleLogoSelect(file);
                          }}
                        />
                      </div>
                      <div style={{ marginTop: 12, textAlign: "right" }}>
                        <button
                          type="button"
                          style={{
                            ...primaryButtonStyle,
                            opacity: logoDirty ? 1 : 0.5,
                          }}
                          disabled={!logoDirty}
                          onClick={handleLogoSubmit}
                        >
                          {logoExists || currentLogoDisplay
                            ? "Save Updated Logo"
                            : "Upload Logo"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </section>
            </div>
          </div>
        );

      case "machines":
        return (
          <div className="info-content-card">
            <header style={contentHeaderStyle}>
              <div>
                <h2 style={contentTitleStyle}>Node Settings</h2>
                <p style={contentSubtitleStyle}>Manage node configurations</p>
              </div>
              {!machineLoading && machines.length > 0 && (
                <button
                  type="button"
                  onClick={() => setExpandAll((prev) => !prev)}
                  style={{
                    background: "none",
                    border: "1px solid #dbe4f0",
                    borderRadius: 8,
                    padding: "8px 16px",
                    fontSize: 13,
                    color: "#334155",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                >
                  {expandAll ? "Collapse All" : "Expand All"}
                </button>
              )}
            </header>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {machineLoading && <p>Loading machines...</p>}
              {!machineLoading && machines.length === 0 && (
                <p>No machines found.</p>
              )}
              {machines.map((machine) => (
                <MachineCard
                  key={machine.machine_id}
                  machine={machine}
                  onUpdate={handleMachineUpdate}
                  forceExpand={expandAll}
                  clientEmails={clientEmailSuggestions}
                />
              ))}
            </div>
          </div>
        );

      case "notifications":
        return (
          <div className="info-content-card">
            <header style={contentHeaderStyle}>
              <h2 style={contentTitleStyle}>Notifications</h2>
              <button type="button" style={primaryButtonStyle}>
                Save preferences
              </button>
            </header>
            <div style={dividerStyle} />

            {/* Report Windows Management */}
            <div className="info-section-group">
              <h3 style={sectionBlockTitleStyle}>Email Notification</h3>
              <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14 }}>
                Manage time range for email reports (e.g., 09 AM - 05 PM).
              </p>

              {/* Add New Window */}
              <div style={formGridStyle}>
                <label style={inputGroupStyle}>
                  Start Time
                  <input
                    type="time"
                    className="eff-time-input"
                    value={newWindow.start}
                    onChange={(e) => setNewWindow({ ...newWindow, start: e.target.value })}
                    onClick={(e) => e.currentTarget.showPicker && e.currentTarget.showPicker()}
                    style={{ ...textInputStyle, cursor: "pointer" }}
                  />
                </label>
                <label style={inputGroupStyle}>
                  End Time
                  <input
                    type="time"
                    className="eff-time-input"
                    value={newWindow.end}
                    onChange={(e) => setNewWindow({ ...newWindow, end: e.target.value })}
                    onClick={(e) => e.currentTarget.showPicker && e.currentTarget.showPicker()}
                    style={{ ...textInputStyle, cursor: "pointer" }}
                  />
                </label>
              </div>
              <div style={{ marginTop: 12, textAlign: "right" }}>
                <button
                  type="button"
                  onClick={handleAddReportWindow}
                  style={primaryButtonStyle}
                >
                  Add Email Time
                </button>
              </div>

              {/* Windows List */}
              {reportWindowLoading ? (
                <p style={{ marginTop: 16 }}>Loading windows...</p>
              ) : (
                <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {reportWindows.length === 0 ? (
                    <p style={{ color: "#94a3b8", fontSize: 14 }}>No report windows configured.</p>
                  ) : (
                    reportWindows.map((win, idx) => {
                      const isEditing = editingWindow &&
                        editingWindow.oldStart === win.start &&
                        editingWindow.oldEnd === win.end;

                      return (
                        <div key={idx} style={{
                          padding: '10px 14px',
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: 8,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>

                          {isEditing ? (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
                              <input
                                type="time"
                                className="eff-time-input"
                                value={editingWindow.newStart}
                                onChange={(e) => setEditingWindow({ ...editingWindow, newStart: e.target.value })}
                                onClick={(e) => e.currentTarget.showPicker && e.currentTarget.showPicker()}
                                style={{ ...textInputStyle, cursor: "pointer" }}
                              />
                              <span>to</span>
                              <input
                                type="time"
                                className="eff-time-input"
                                value={editingWindow.newEnd}
                                onChange={(e) => setEditingWindow({ ...editingWindow, newEnd: e.target.value })}
                                onClick={(e) => e.currentTarget.showPicker && e.currentTarget.showPicker()}
                                style={{ ...textInputStyle, cursor: "pointer" }}
                              />
                            </div>
                          ) : (
                            <div>
                              <span style={{ fontWeight: 500, color: '#334155' }}>
                                {win.start} - {win.end}
                              </span>
                            </div>
                          )}

                          <div style={{ display: 'flex', gap: 8 }}>
                            {isEditing ? (
                              <>
                                <button
                                  onClick={saveEditedReportWindow}
                                  style={{ ...actionButtonStyle, color: '#16a34a' }}
                                >
                                  Save
                                </button>
                                <button
                                  onClick={cancelEditingReportWindow}
                                  style={{ ...actionButtonStyle, color: '#64748b' }}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => startEditingReportWindow(win)}
                                  style={{ ...actionButtonStyle, color: '#2563eb' }}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteReportWindow(win)}
                                  style={{ ...actionButtonStyle, color: '#ef4444' }}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            <div style={dividerStyle} />

            {/* Energy Efficiency Report Schedule */}
            <div className="info-section-group">
              <h3 style={sectionBlockTitleStyle}>Energy Efficiency Report Schedule</h3>
              <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14 }}>
                Schedule time windows to email the Energy Efficiency Report, and
                choose who receives each one. Times are 24-hour (HH:MM).
              </p>

              {effLoading ? (
                <p style={{ color: "#64748b", fontSize: 14 }}>Loading schedule...</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {effSchedule.length === 0 && (
                    <p style={{ color: "#94a3b8", fontSize: 14 }}>
                      No schedules yet. Click “Add Schedule” to create one.
                    </p>
                  )}

                  {effSchedule.map((entry, idx) => (
                    <div
                      key={idx}
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        padding: 14,
                        background: "#f8fafc",
                      }}
                    >
                      {/* Time row */}
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                        <label style={{ ...inputGroupStyle, flex: "0 0 auto" }}>
                          Start Time
                          <input
                            type="time"
                            className="eff-time-input"
                            value={entry.start}
                            onChange={(e) => updateEffTime(idx, "start", e.target.value)}
                            onClick={(e) => e.currentTarget.showPicker && e.currentTarget.showPicker()}
                            style={{ ...textInputStyle, width: 140, cursor: "pointer" }}
                          />
                        </label>
                        <label style={{ ...inputGroupStyle, flex: "0 0 auto" }}>
                          End Time
                          <input
                            type="time"
                            className="eff-time-input"
                            value={entry.end}
                            onChange={(e) => updateEffTime(idx, "end", e.target.value)}
                            onClick={(e) => e.currentTarget.showPicker && e.currentTarget.showPicker()}
                            style={{ ...textInputStyle, width: 140, cursor: "pointer" }}
                          />
                        </label>
                        <div style={{ marginLeft: "auto" }}>
                          <button
                            type="button"
                            onClick={() => removeEffEntry(idx)}
                            style={{ ...actionButtonStyle, color: "#ef4444" }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      {/* Emails */}
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
                          Recipients
                        </div>
                        {entry.emails.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                            {entry.emails.map((email) => (
                              <span
                                key={email}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "4px 10px",
                                  background: "#e6f7f5",
                                  color: "#0f766e",
                                  borderRadius: 999,
                                  fontSize: 12,
                                  fontWeight: 500,
                                }}
                              >
                                {email}
                                <button
                                  type="button"
                                  onClick={() => removeEffEmail(idx, email)}
                                  style={{ background: "none", border: "none", color: "#0f766e", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 0 }}
                                  aria-label={`Remove ${email}`}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <input
                            type="email"
                            value={effEmailInput[idx] || ""}
                            onChange={(e) =>
                              setEffEmailInput((prev) => ({ ...prev, [idx]: e.target.value }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addEffEmail(idx);
                              }
                            }}
                            placeholder="Add recipient email"
                            style={{ ...textInputStyle, flex: "1 1 220px", minWidth: 200 }}
                          />
                          <button
                            type="button"
                            onClick={() => addEffEmail(idx)}
                            style={{ ...primaryButtonStyle, padding: "8px 16px" }}
                          >
                            Add
                          </button>
                        </div>
                      </div>

                      {entry.last_sent && (
                        <div style={{ marginTop: 8, fontSize: 12, color: "#94a3b8" }}>
                          Last sent: {entry.last_sent}
                        </div>
                      )}
                    </div>
                  ))}

                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 4 }}>
                    <button
                      type="button"
                      onClick={addEffEntry}
                      style={{
                        background: "none",
                        border: "1px dashed #cbd5e1",
                        borderRadius: 8,
                        padding: "8px 16px",
                        fontSize: 13,
                        color: "#334155",
                        cursor: "pointer",
                        fontWeight: 500,
                      }}
                    >
                      + Add Schedule
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveEffSchedule}
                      disabled={effSaving}
                      style={{
                        ...primaryButtonStyle,
                        opacity: effSaving ? 0.6 : 1,
                        cursor: effSaving ? "not-allowed" : "pointer",
                      }}
                    >
                      {effSaving ? "Saving..." : "Save Schedule"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div style={dividerStyle} />

            {/* Alert Threshold Settings */}
            <div className="info-section-group">
              <h3 style={sectionBlockTitleStyle}>Alert Thresholds</h3>
              <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14 }}>
                Enable or disable background alert checks for sanctioned load and minimum threshold.
              </p>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>

                {/* Sanctioned Load Card */}
                <div style={thresholdCardStyle}>
                  <p style={thresholdCardTitleStyle}>Sanctioned Load</p>
                  <p style={thresholdUnitStyle}>kW</p>
                  <input
                    style={textInputStyle}
                    type="number"
                    value={profileSettings.sanctioned_load ?? ""}
                    readOnly
                  />
                  <button
                    style={{
                      ...togglePillStyle,
                      backgroundColor: profileSettings.sanctioned_load_on ? "#1BA08E" : "#d9d9d9",
                      color: profileSettings.sanctioned_load_on ? "#fff" : "#555",
                    }}
                    disabled={togglingField === "sanctioned_load_on"}
                    onClick={() => handleToggleSetting("sanctioned_load_on")}
                  >
                    {profileSettings.sanctioned_load_on ? "Enabled" : "Disabled"}
                  </button>
                </div>

                {/* Minimum Threshold Card */}
                <div style={thresholdCardStyle}>
                  <p style={thresholdCardTitleStyle}>Minimum Threshold</p>
                  <p style={thresholdUnitStyle}>kW</p>
                  <input
                    style={textInputStyle}
                    type="number"
                    value={localThreshold}
                    onChange={(e) => setLocalThreshold(e.target.value)}
                    onBlur={async () => {
                      const val = parseFloat(localThreshold);
                      if (!isNaN(val)) {
                        try {
                          await updateClientProfileSettings(username, { minimum_threshold: val });
                          setProfileSettings(prev => ({ ...prev, minimum_threshold: val }));
                          showToast("Threshold value saved", "success");
                        } catch (e) {
                          showToast("Failed to save threshold", "error");
                        }
                      }
                    }}
                  />
                  <button
                    style={{
                      ...togglePillStyle,
                      backgroundColor: profileSettings.minimum_threshold_on ? "#1BA08E" : "#d9d9d9",
                      color: profileSettings.minimum_threshold_on ? "#fff" : "#555",
                    }}
                    disabled={togglingField === "minimum_threshold_on"}
                    onClick={() => handleToggleSetting("minimum_threshold_on", {
                      minimum_threshold: parseFloat(localThreshold) || profileSettings.minimum_threshold,
                    })}
                  >
                    {profileSettings.minimum_threshold_on ? "Enabled" : "Disabled"}
                  </button>
                </div>

              </div>
            </div>
          </div>
        );

      case "multiTier":
        return (
          <div className="info-content-card">
            <header style={contentHeaderStyle}>
              <div>
                <h2 style={contentTitleStyle}>Add Credentials</h2>
                <p style={{ margin: "4px 0 0", color: "#94a3b8", fontSize: 14 }}>
                  Create user credentials for specific machines
                </p>
              </div>
            </header>
            <div style={dividerStyle} />

            <div className="info-section-card">
              <div style={formGridStyle}>
                <label style={inputGroupStyle}>
                  Username
                  <input
                    type="text"
                    value={multiTierForm.username}
                    onChange={(e) => setMultiTierForm({ ...multiTierForm, username: e.target.value })}
                    style={textInputStyle}
                    placeholder="Enter username"
                  />
                </label>
                <label style={inputGroupStyle}>
                  Password
                  <input
                    type="password"
                    value={multiTierForm.password}
                    onChange={(e) => setMultiTierForm({ ...multiTierForm, password: e.target.value })}
                    style={textInputStyle}
                    placeholder="Enter password"
                  />
                </label>
                <div style={{ ...inputGroupStyle, position: 'relative' }}>
                  <label style={{ fontSize: 14, color: "#0f172a", marginBottom: 6 }}>Primary Node</label>
                  {mtLoading ? (
                    <div style={{ ...textInputStyle, color: '#94a3b8' }}>Loading machines...</div>
                  ) : (
                    <NestedMachineSelector
                      isFormMode={true}
                      hierarchyData={mtMachinesTree}
                      selectedNode={{ name: multiTierForm.machineName, meter_id: multiTierForm.meterId }}
                      onSelect={(node) => {
                        setMultiTierForm({
                          ...multiTierForm,
                          meterId: node.meter_id,
                          machineName: node.name,
                          excludedIds: [] // reset exclusions on new selection
                        });
                        setMtSelectedNodeObj(node.rawNode);
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Exclusion UI appears when a primary node is selected */}
              {mtSelectedNodeObj && (
                <div style={{ marginTop: 16 }}>
                  <ExcludeNodeTree
                    treeNode={mtSelectedNodeObj}
                    excludedIds={multiTierForm.excludedIds}
                    onToggleExclude={(id) => {
                      setMultiTierForm(prev => {
                        const newExcludes = prev.excludedIds.includes(id)
                          ? prev.excludedIds.filter(eId => eId !== id)
                          : [...prev.excludedIds, id];
                        return { ...prev, excludedIds: newExcludes };
                      });
                    }}
                  />
                </div>
              )}

              <div style={{ marginTop: 20, textAlign: "right" }}>
                <button
                  type="button"
                  onClick={handleAddCredentials}
                  style={primaryButtonStyle}
                  disabled={mtLoading}
                >
                  Save Information
                </button>
              </div>
            </div>

            <div className="info-section-card">
              <h3 style={{ ...sectionBlockTitleStyle, marginBottom: 4 }}>Recently Added</h3>
              {addedCredentials.length === 0 ? (
                <p style={{ color: "#94a3b8", fontSize: 14 }}>No credentials added yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {addedCredentials.map((cred, index) => (
                    <div key={index} style={{
                      padding: '12px 16px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: 14
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 600, color: '#334155' }}>
                          User: {cred.username}
                        </span>
                        <span style={{ fontSize: 12, color: '#64748b' }}>
                          Machine: {cred.machineName || "Unknown"}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: '#94a3b8' }}>
                        Added {cred.timestamp}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="info-tab-page">
      <header className="info-tab-header">
      </header>

      <section className="info-tab-layout">
        <aside className="info-tab-sidebar">
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => setActiveSection(section.id)}
              style={sidebarItemStyle(activeSection === section.id)}
            >
              <span style={{ fontWeight: 600 }}>{section.title}</span>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>
                {section.subtitle}
              </span>
            </button>
          ))}
        </aside>

        <div className="info-tab-content">{renderSectionContent()}</div>
      </section>

      {/* Toast notification */}
      {toast.show && (
        <div style={toastStyle(toast.type)}>
          <span style={{ marginRight: 8, fontSize: 16 }}>
            {toast.type === "success" ? "\u2713" : "\u2717"}
          </span>
          {toast.message}
        </div>
      )}
    </div>
  );
};

const loadTypeOptions = [
  {
    label: "Generation",
    value: "generation",
    children: [
      { label: "Grid", value: "grid" },
      { label: "Solar", value: "solar" },
      { label: "Generator", value: "generator" },
      { label: "Wind mill", value: "wind_mill" },
    ],
  },
  {
    label: "Load",
    value: "load",
    children: [
      { label: "Motor", value: "motor" },
      { label: "Cooling", value: "cooling" },
      { label: "Heating", value: "heating" },
      { label: "Lighting", value: "lighting" },
      { label: "Electronics", value: "electronics" },
      { label: "Electrolysis", value: "electrolysis" },
    ],
  },
  {
    label: "Storage",
    value: "storage",
    children: [
      { label: "UPS", value: "ups" },
      { label: "BESS", value: "bess" },
    ],
  },
  { label: "Building with Solar", value: "building_with_solar" },
  { label: "Building without Solar", value: "building_without_solar" },
];

const HierarchicalSelector = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Recursively find the label for the current value
  const getLabel = (options, val) => {
    for (const opt of options) {
      if (opt.value === val && !opt.children) return opt.label;
      if (opt.children) {
        const found = getLabel(opt.children, val);
        if (found) return found;
      }
    }
    return val || "Select Load Type";
  };

  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
  };

  return (
    <div style={{ position: "relative" }} ref={dropdownRef}>
      <div
        style={{
          ...textInputStyle,
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#fff",
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{getLabel(loadTypeOptions, value)}</span>
        <span style={{ fontSize: 10, opacity: 0.6 }}>▼</span>
      </div>

      {isOpen && (
        <div style={dropdownMenuStyle}>
          {loadTypeOptions.map((option) => (
            <DropdownItem
              key={option.label}
              option={option}
              onSelect={handleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DropdownItem = ({ option, onSelect }) => {
  const [isHovered, setIsHovered] = useState(false);

  const hasChildren = option.children && option.children.length > 0;

  const handleClick = (e) => {
    if (!hasChildren) {
      e.stopPropagation();
      onSelect(option.value);
    }
  };

  return (
    <div
      style={{
        ...dropdownItemStyle,
        background: isHovered ? "#f1f5f9" : "#fff",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <span>{option.label}</span>
        {hasChildren && <span style={{ fontSize: 10, opacity: 0.6 }}>▶</span>}
      </div>

      {hasChildren && isHovered && (
        <div style={submenuStyle}>
          {option.children.map((child) => (
            <DropdownItem
              key={child.label}
              option={child}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Sub-component for Machine Card
const MachineCard = ({ machine, onUpdate, forceExpand, clientEmails = [] }) => {
  const [config, setConfig] = useState({
    load_type: machine.load_type,
    active_alert: machine.active_alert,
    inactive_alert: machine.inactive_alert,
    rated_power_alert: machine.rated_power_alert,
    inactive_threshold_minutes: machine.inactive_threshold_minutes,
    active_threshold_minutes: machine.active_threshold_minutes,
    color: machine.color,
    start_hour: machine.start_hour,
    end_hour: machine.end_hour,
    rated_power: machine.rated_power,
  });
  const [isDirty, setIsDirty] = useState(false);
  const [isExpanded, setIsExpanded] = useState(forceExpand || false);

  // Per-machine alert email recipients
  const [alertEmails, setAlertEmails] = useState([]);
  const [alertEmailsLoaded, setAlertEmailsLoaded] = useState(false);
  const [alertEmailsLoading, setAlertEmailsLoading] = useState(false);
  const [alertEmailsDirty, setAlertEmailsDirty] = useState(false);
  const [customEmailInput, setCustomEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const anyAlertEnabled =
    config.rated_power_alert || config.active_alert || config.inactive_alert;

  useEffect(() => {
    setIsExpanded(forceExpand);
  }, [forceExpand]);

  // Lazy-load saved alert recipients when the card is first expanded
  useEffect(() => {
    if (!isExpanded || alertEmailsLoaded || !machine.meter_id) return;
    setAlertEmailsLoading(true);
    getMachineAlertEmails(machine.meter_id, machine.machine_name)
      .then((emails) => setAlertEmails(emails || []))
      .catch((err) => {
        console.error("Failed to load machine alert emails", err);
        setAlertEmails([]);
      })
      .finally(() => {
        setAlertEmailsLoaded(true);
        setAlertEmailsLoading(false);
      });
  }, [isExpanded, alertEmailsLoaded, machine.meter_id, machine.machine_name]);

  const handleChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

  const addAlertEmail = (email) => {
    const trimmed = (email || "").trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      setEmailError("Invalid email format");
      return;
    }
    if (alertEmails.includes(trimmed)) {
      setEmailError("Already added");
      return;
    }
    setAlertEmails((prev) => [...prev, trimmed]);
    setAlertEmailsDirty(true);
    setEmailError("");
  };

  const removeAlertEmail = (email) => {
    setAlertEmails((prev) => prev.filter((e) => e !== email));
    setAlertEmailsDirty(true);
  };

  const handeSave = async () => {
    if (isDirty) {
      onUpdate(machine.machine_id, config);
      setIsDirty(false);
    }
    if (alertEmailsDirty && machine.meter_id) {
      try {
        await setMachineAlertEmails(
          machine.meter_id,
          machine.machine_name,
          alertEmails,
        );
        setAlertEmailsDirty(false);
      } catch (err) {
        console.error("Failed to save alert emails", err);
      }
    }
  };

  const cardDirty = isDirty || alertEmailsDirty;

  // Summary of active alerts for collapsed view
  const alertSummary = [];
  if (config.rated_power_alert) alertSummary.push("Power");
  if (config.active_alert) alertSummary.push("Outside Hours");
  if (config.inactive_alert) alertSummary.push("Inactive");

  return (
    <div style={{
      backgroundColor: "#fff",
      borderRadius: 12,
      boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
      border: cardDirty ? "1px solid #1BA08E" : "1px solid #e2e8f0",
      overflow: "visible",
    }}>
      {/* Collapsed Header - always visible */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 20px",
          cursor: "pointer",
          background: isExpanded ? "#f8fafc" : "#fff",
          userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: config.color || "#000",
            border: "1px solid #e2e8f0",
            flexShrink: 0,
          }} />
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{machine.machine_name}</h3>
          {config.rated_power && (
            <span style={{ fontSize: 12, color: "#64748b", background: "#f1f5f9", padding: "2px 8px", borderRadius: 6 }}>
              {config.rated_power} kW
            </span>
          )}
          {alertSummary.length > 0 && (
            <span style={{ fontSize: 12, color: "#1BA08E", background: "#e6f7f5", padding: "2px 8px", borderRadius: 6 }}>
              {alertSummary.join(", ")}
            </span>
          )}
          {cardDirty && (
            <span style={{ fontSize: 11, color: "#f59e0b", fontWeight: 600 }}>Unsaved</span>
          )}
        </div>
        <span style={{ fontSize: 12, color: "#94a3b8", transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>
          ▼
        </span>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{ padding: "0 20px 20px" }}>

          {/* All settings in one row-based layout */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>

            {/* Left: Machine config */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: "1 1 300px" }}>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
                <label style={{ ...inputGroupStyle, flex: 1 }}>
                  Rated Power (kW)
                  <input
                    type="number"
                    step="0.1"
                    value={config.rated_power || ""}
                    onChange={(e) => handleChange("rated_power", e.target.value)}
                    style={textInputStyle}
                    placeholder="e.g. 50.5"
                  />
                </label>
                <label style={{ ...inputGroupStyle, flex: 1 }}>
                  Load Type
                  <HierarchicalSelector
                    value={config.load_type}
                    onChange={(val) => handleChange("load_type", val)}
                  />
                </label>
                <label style={inputGroupStyle}>
                  Color
                  <input
                    type="color"
                    value={config.color || "#000000"}
                    onChange={(e) => handleChange("color", e.target.value)}
                    style={{ padding: 0, height: 38, width: 44, borderRadius: 8, border: "1px solid #dbe4f0", cursor: "pointer" }}
                  />
                </label>
              </div>
            </div>

            {/* Right: Alerts */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: "1 1 300px", justifyContent: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={config.rated_power_alert || false}
                  onChange={(e) => handleChange("rated_power_alert", e.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                <span style={{ fontSize: 14, fontWeight: 500, color: "#334155" }}>Alert if rated power exceeded</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={config.active_alert || false}
                  onChange={(e) => handleChange("active_alert", e.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                <span style={{ fontSize: 14, fontWeight: 500, color: "#334155" }}>Alert if active outside hours</span>
                {config.active_alert && (
                  <>
                    <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>after</span>
                    <input
                      type="number"
                      min="1"
                      value={config.active_threshold_minutes || ""}
                      onChange={(e) => handleChange("active_threshold_minutes", parseInt(e.target.value) || "")}
                      style={{ ...textInputStyle, width: 55, padding: "4px 8px", textAlign: "center" }}
                      placeholder="15"
                    />
                    <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>min</span>
                  </>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={config.inactive_alert || false}
                  onChange={(e) => handleChange("inactive_alert", e.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                <span style={{ fontSize: 14, fontWeight: 500, color: "#334155" }}>Alert if inactive during hours</span>
                {config.inactive_alert && (
                  <>
                    <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>after</span>
                    <input
                      type="number"
                      min="1"
                      value={config.inactive_threshold_minutes || ""}
                      onChange={(e) => handleChange("inactive_threshold_minutes", parseInt(e.target.value) || "")}
                      style={{ ...textInputStyle, width: 55, padding: "4px 8px", textAlign: "center" }}
                      placeholder="15"
                    />
                    <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>min</span>
                  </>
                )}
              </div>
              {(config.active_alert || config.inactive_alert) && (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>Working Hours</span>
                  <select
                    value={config.start_hour || ""}
                    onChange={(e) => handleChange("start_hour", e.target.value)}
                    style={{ ...textInputStyle, width: 100, padding: "8px 10px" }}
                  >
                    <option value="">Start</option>
                    {Array.from({ length: 24 }, (_, i) => {
                      const h = i.toString().padStart(2, "0") + ":00";
                      return <option key={h} value={h}>{h}</option>;
                    })}
                  </select>
                  <span style={{ fontSize: 14, color: "#64748b" }}>to</span>
                  <select
                    value={config.end_hour || ""}
                    onChange={(e) => handleChange("end_hour", e.target.value)}
                    style={{ ...textInputStyle, width: 100, padding: "8px 10px" }}
                  >
                    <option value="">End</option>
                    {Array.from({ length: 24 }, (_, i) => {
                      const h = i.toString().padStart(2, "0") + ":00";
                      return <option key={h} value={h}>{h}</option>;
                    })}
                  </select>
                </div>
              )}
            </div>

          </div>

          {/* Alert Email Recipients (per-machine) */}
          {anyAlertEnabled && (
            <div
              style={{
                marginTop: 12,
                padding: "12px 14px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#334155",
                  marginBottom: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Alert Emails
                <span style={{ fontWeight: 400, color: "#94a3b8" }}>
                  (optional)
                </span>
                <span
                  title="Alerts always appear in the dashboard notification icon. Add emails here only if you also want to receive them by email."
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    border: "1px solid #cbd5e1",
                    color: "#64748b",
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontStyle: "italic",
                    fontFamily: "serif",
                  }}
                  aria-label="Info: about alert emails"
                >
                  i
                </span>
              </div>

              {alertEmailsLoading ? (
                <div style={{ fontSize: 13, color: "#64748b" }}>
                  Loading...
                </div>
              ) : (
                <>
                  {/* Selected emails as chips */}
                  {alertEmails.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginBottom: 8,
                    }}
                  >
                    {alertEmails.map((email) => (
                      <span
                        key={email}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "4px 10px",
                          background: "#e6f7f5",
                          color: "#0f766e",
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => removeAlertEmail(email)}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#0f766e",
                            cursor: "pointer",
                            fontSize: 14,
                            lineHeight: 1,
                            padding: 0,
                          }}
                          aria-label={`Remove ${email}`}
                          title="Remove"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  )}

                  {/* Single combobox — type or pick from suggestions */}
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ position: "relative", flex: "1 1 240px", minWidth: 240 }}>
                      <input
                        type="email"
                        value={customEmailInput}
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                        onChange={(e) => {
                          setCustomEmailInput(e.target.value);
                          if (emailError) setEmailError("");
                          setShowSuggestions(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addAlertEmail(customEmailInput);
                            if (isValidEmail(customEmailInput)) {
                              setCustomEmailInput("");
                            }
                          }
                          if (e.key === "Escape") setShowSuggestions(false);
                        }}
                        placeholder="Type or pick an email"
                        style={{
                          ...textInputStyle,
                          padding: "6px 10px",
                          fontSize: 13,
                          width: "100%",
                          boxSizing: "border-box",
                        }}
                      />
                      {showSuggestions && (() => {
                        const q = customEmailInput.trim().toLowerCase();
                        const filtered = clientEmails
                          .filter((e) => !alertEmails.includes(e))
                          .filter((e) => !q || e.toLowerCase().includes(q));
                        if (filtered.length === 0) return null;
                        return (
                          <ul
                            style={{
                              position: "absolute",
                              top: "calc(100% + 4px)",
                              left: 0,
                              right: 0,
                              margin: 0,
                              padding: 4,
                              listStyle: "none",
                              background: "#fff",
                              border: "1px solid #e2e8f0",
                              borderRadius: 8,
                              boxShadow: "0 6px 16px rgba(15, 23, 42, 0.08)",
                              maxHeight: 180,
                              overflowY: "auto",
                              zIndex: 20,
                            }}
                          >
                            {filtered.map((email) => (
                              <li
                                key={email}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  addAlertEmail(email);
                                  setCustomEmailInput("");
                                  setShowSuggestions(false);
                                }}
                                style={{
                                  padding: "6px 10px",
                                  borderRadius: 6,
                                  fontSize: 13,
                                  color: "#0f172a",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                              >
                                {email}
                              </li>
                            ))}
                          </ul>
                        );
                      })()}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        addAlertEmail(customEmailInput);
                        if (isValidEmail(customEmailInput)) {
                          setCustomEmailInput("");
                        }
                      }}
                      style={{
                        ...primaryButtonStyle,
                        padding: "6px 14px",
                        fontSize: 13,
                      }}
                    >
                      Add
                    </button>
                  </div>

                  {emailError && (
                    <div
                      style={{
                        fontSize: 12,
                        color: "#dc2626",
                        marginTop: 6,
                      }}
                    >
                      {emailError}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div style={{ textAlign: "right", marginTop: 4 }}>
            <button
              type="button"
              onClick={handeSave}
              disabled={!cardDirty}
              style={{ ...primaryButtonStyle, opacity: cardDirty ? 1 : 0.5, padding: "8px 16px" }}
            >
              Save
            </button>
          </div>

        </div>
      )}
    </div>
  );
};

// Styles
const sidebarItemStyle = (active) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: "12px 16px",
  borderRadius: 12,
  border: active ? "1px solid #2563eb" : "1px solid #e2e8f0",
  background: active ? "#e0ecff" : "#fff",
  cursor: "pointer",
  gap: 4,
});

const contentHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 16,
};

const contentTitleStyle = {
  margin: 0,
};

const contentSubtitleStyle = {
  margin: "4px 0 0",
  color: "#94a3b8",
};

const formGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 16,
  overflow: "visible", // Changed to prevent clipping of dropdowns
};

const inputGroupStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  fontSize: 14,
  color: "#0f172a",
};

const textInputStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #dbe4f0",
  fontSize: 14,
};

const primaryButtonStyle = {
  padding: "10px 18px",
  borderRadius: 12,
  border: "none",
  background: "linear-gradient(135deg, #1BA08E 60%, #17a98b 100%)",
  color: "white",
  fontWeight: 600,
  cursor: "pointer",
  boxShadow: "0 10px 20px rgba(27,160,142,0.15)",
  transition: "background 0.2s",
};

const actionButtonStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "6px 10px",
  fontSize: 14,
  fontWeight: 500,
  borderRadius: 8,
  transition: "background 0.2s",
};

const sectionBlockTitleStyle = {
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: "#334155",
};

const sectionBlockDescriptionStyle = {
  margin: "4px 0 0",
  fontSize: 13,
  color: "#64748b",
};

const dividerStyle = {
  height: 0,
  background: "transparent",
  border: "none",
  margin: 0,
};

const toastStyle = (type) => ({
  position: "fixed",
  bottom: 24,
  right: 24,
  padding: "14px 24px",
  borderRadius: 12,
  background: type === "success" ? "#065f46" : "#991b1b",
  color: "#fff",
  fontSize: 14,
  fontWeight: 500,
  boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  animation: "slideIn 0.3s ease-out",
});

const toggleRowStyle = {
  display: "flex",
  gap: 12,
  padding: "12px 16px",
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
};

const toggleHelpTextStyle = {
  margin: 0,
  color: "#94a3b8",
  fontSize: 13,
};

const logoUploadWrapperStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const logoDisplayWrapperStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 12,
  padding: 24,
  borderRadius: 16,
  background: "#f8fafc",
  border: "1px solid #dbe4f0",
};

const logoDisplayStyle = {
  maxWidth: "100%",
  maxHeight: 200,
  objectFit: "contain",
  borderRadius: 8,
};

const editLogoButtonStyle = {
  padding: "8px 16px",
  borderRadius: 8,
  border: "1px solid #dbe4f0",
  background: "#fff",
  color: "#0f172a",
  fontWeight: 500,
  cursor: "pointer",
  fontSize: 14,
  transition: "all 0.2s ease",
};

const logoDropAreaStyle = {
  border: "2px dashed #cbd5f5",
  borderRadius: 16,
  padding: 24,
  textAlign: "center",
  background: "#f8fafc",
  color: "#94a3b8",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 160,
};

const dropdownMenuStyle = {
  position: "absolute",
  top: "100%",
  left: 0,
  width: "100%",
  minWidth: 160,
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  boxShadow:
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  zIndex: 50,
  marginTop: 4,
  padding: "4px 0",
};

const dropdownItemStyle = {
  padding: "8px 12px",
  cursor: "pointer",
  fontSize: 14,
  color: "#334155",
  position: "relative",
  userSelect: "none",
};

const submenuStyle = {
  position: "absolute",
  top: 0,
  left: "100%",
  width: "100%",
  minWidth: 160,
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  marginLeft: 4,
  padding: "4px 0",
};

const thresholdCardStyle = {
  flex: "1 1 220px",
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
  padding: "16px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const thresholdCardTitleStyle = {
  margin: 0,
  fontSize: 15,
  fontWeight: 600,
  color: "#334155",
};

const thresholdUnitStyle = {
  margin: 0,
  fontSize: 12,
  color: "#1BA08E",
  fontWeight: 600,
};

const togglePillStyle = {
  alignSelf: "flex-end",
  padding: "6px 20px",
  fontSize: 13,
  fontWeight: 600,
  border: "none",
  borderRadius: 20,
  cursor: "pointer",
  transition: "background-color 0.2s",
};

export default InfoTab;
